import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import { flattenToWireLines } from '../../features/pack-analysis-workbench/components/Simulator/lineModel';

export const selectWorkbenchState = (state: RootState) => state.packAnalysisWorkbench;

// ── Ingest ─────────────────────────────────────────────────────────────────

export const selectIngestInFlight = (state: RootState) =>
  state.packAnalysisWorkbench.ingest.isInFlight;
export const selectIngestError = (state: RootState) =>
  state.packAnalysisWorkbench.ingest.error;
export const selectIngestHistory = (state: RootState) =>
  state.packAnalysisWorkbench.ingest.history;
export const selectLastIngestBatch = (state: RootState) =>
  state.packAnalysisWorkbench.ingest.history[0] ?? null;
export const selectIngestProgress = (state: RootState) =>
  state.packAnalysisWorkbench.ingest.progress;
export const selectIngestPerFile = (state: RootState) =>
  state.packAnalysisWorkbench.ingest.perFile;
export const selectIngestPerFileTotal = (state: RootState) =>
  state.packAnalysisWorkbench.ingest.perFileTotal;
export const selectIngestIsCancelling = (state: RootState) =>
  state.packAnalysisWorkbench.ingest.isCancelling;
export const selectIngestWasCancelled = (state: RootState) =>
  state.packAnalysisWorkbench.ingest.wasCancelled;
export const selectIngestCurrentBatchId = (state: RootState) =>
  state.packAnalysisWorkbench.ingest.currentBatchId;

// Spec pack-analysis-ingest-background-progress — global ingest visibility.
export const selectActiveBatch = (state: RootState) =>
  state.packAnalysisWorkbench.ingest.activeBatch;
export const selectIsIngestActive = (state: RootState) =>
  state.packAnalysisWorkbench.ingest.activeBatch != null;
export const selectActiveBatchPercent = createSelector(
  [selectActiveBatch],
  (batch) => {
    if (batch == null || batch.totalFiles <= 0) return null;
    return Math.round((batch.completedFiles / batch.totalFiles) * 100);
  }
);
export const selectLastCompletedBatchSnapshot = (state: RootState) =>
  state.packAnalysisWorkbench.ingest.lastCompletedBatchSnapshot;
export const selectNotificationPermissionPrompted = (state: RootState) =>
  state.packAnalysisWorkbench.ingest.notificationPermissionPrompted;

// ── Browser ────────────────────────────────────────────────────────────────

export const selectAsnList = (state: RootState) => state.packAnalysisWorkbench.browser.list;
export const selectAsnTotalCount = (state: RootState) =>
  state.packAnalysisWorkbench.browser.totalCount;
export const selectAsnPageIndex = (state: RootState) =>
  state.packAnalysisWorkbench.browser.pageIndex;
export const selectAsnPageSize = (state: RootState) =>
  state.packAnalysisWorkbench.browser.pageSize;
export const selectAsnFilters = (state: RootState) =>
  state.packAnalysisWorkbench.browser.filters;
export const selectAsnSortBy = (state: RootState) =>
  state.packAnalysisWorkbench.browser.sortBy;
export const selectAsnSortDir = (state: RootState) =>
  state.packAnalysisWorkbench.browser.sortDir;

/**
 * pack-analysis-asn-browser-pagination-v2 — bundled view-state for `fetchAsnList`
 * + the URL-sync hook. Memoized so the URL writeback effect doesn't churn on
 * unrelated slice changes (e.g. ingest progress).
 */
export const selectAsnListParams = createSelector(
  [
    selectAsnFilters,
    selectAsnPageIndex,
    selectAsnPageSize,
    selectAsnSortBy,
    selectAsnSortDir,
  ],
  (filters, pageIndex, pageSize, sortBy, sortDir) => ({
    status: filters.status,
    dateFrom: filters.dateFrom ?? null,
    dateTo: filters.dateTo ?? null,
    pageIndex,
    pageSize,
    sortBy: sortBy ?? null,
    sortDir: sortDir ?? null,
  })
);
export const selectAsnListLoading = (state: RootState) =>
  state.packAnalysisWorkbench.browser.isListLoading;
export const selectAsnListError = (state: RootState) =>
  state.packAnalysisWorkbench.browser.listError;

export const selectAsnDetailById = (doc856Id: number) =>
  createSelector(
    [(state: RootState) => state.packAnalysisWorkbench.browser.detailById],
    (cache) => cache[doc856Id] ?? null
  );

export const selectAsnDetailLoading = (state: RootState) =>
  state.packAnalysisWorkbench.browser.isDetailLoading;
export const selectAsnDetailError = (state: RootState) =>
  state.packAnalysisWorkbench.browser.detailError;

// ── Simulator ──────────────────────────────────────────────────────────────

export const selectSimulatorSelected = (state: RootState) =>
  state.packAnalysisWorkbench.simulator.selectedPackMaster;
export const selectSimulatorResult = (state: RootState) =>
  state.packAnalysisWorkbench.simulator.lastResult;
export const selectSimulatorLoading = (state: RootState) =>
  state.packAnalysisWorkbench.simulator.isSimulating;
export const selectSimulatorError = (state: RootState) =>
  state.packAnalysisWorkbench.simulator.error;

// Phase E — PO header / criteria / lines / picker-open
export const selectSimulatorPoHeader = (state: RootState) =>
  state.packAnalysisWorkbench.simulator.poHeader;
export const selectSimulatorCriteria = (state: RootState) =>
  state.packAnalysisWorkbench.simulator.criteria;
export const selectSimulatorPoLines = (state: RootState) =>
  state.packAnalysisWorkbench.simulator.poLines;
export const selectSimulatorPickerOpen = (state: RootState) =>
  state.packAnalysisWorkbench.simulator.pickerOpen;

// Spec pack-analysis-simulator-item-aware-line-entry — flatten the editor lines
// to the cartonization request's `lines[]` (strip `_*` FE-only fields, drop
// blank / qty≤0 rows). Memoized so `SimulatorTab` / `SimulatorResultPanel` get
// a stable array reference when nothing relevant changed.
export const selectSimulatorWireLines = createSelector(
  [selectSimulatorPoLines],
  (poLines) => flattenToWireLines(poLines)
);
export const selectSimulatorItemSearch = (state: RootState) =>
  state.packAnalysisWorkbench.simulator.itemSearch;
export const selectSimulatorItemMasterPickerOpen = (state: RootState) =>
  state.packAnalysisWorkbench.simulator.itemMasterPickerOpen;
export const selectSimulatorPickerTargetLineId = (state: RootState) =>
  state.packAnalysisWorkbench.simulator.pickerTargetLineId;
export const selectSimulatorPrepackGroups = (state: RootState) =>
  state.packAnalysisWorkbench.simulator.prepackGroups;

export const selectSimulatorPickerItems = (state: RootState) =>
  state.packAnalysisWorkbench.simulator.pickerItems;
export const selectSimulatorPickerTotalCount = (state: RootState) =>
  state.packAnalysisWorkbench.simulator.pickerTotalCount;
export const selectSimulatorPickerLoading = (state: RootState) =>
  state.packAnalysisWorkbench.simulator.isPickerLoading;
export const selectSimulatorPickerError = (state: RootState) =>
  state.packAnalysisWorkbench.simulator.pickerError;

// ── Pack Planner (spec pack-planner Req 1.4 — parallel sub-slice) ──────────

/**
 * Legacy "effective result" selector — kept for backward compat. Prefers
 * progressive (the canonical Plan card source); falls back to alternatives
 * when progressive failed but alternatives succeeded (spec
 * pack-planner-build-timeline Req 11.6).
 */
export const selectPackPlannerResult = (state: RootState) => {
  // Re-run race fix (2026-05-27): the alternatives request can fulfilled
  // BEFORE progressive on a re-click. In IncludeAlternatives mode the BE
  // response's `cartons[]` is ONLY the first packed strategy's cartons
  // (PackSuggester.cs ~L418), so a naive `progressive ?? alternatives`
  // fallback would briefly render the alternatives-shape cartons (e.g.
  // just T1 → 200 packed → "550 unpacked" flash on a 750-ordered PO).
  // Per Req 11.6 the fallback is meant for progressive-FAILED, not
  // progressive-still-loading — so suppress the fallback while progressive
  // is in flight. Once progressive resolves (fulfilled or rejected), the
  // fallback engages normally.
  const sub = state.packAnalysisWorkbench.packPlanner;
  if (sub.progressiveResult) return sub.progressiveResult;
  if (sub.isLoadingProgressive) return null;
  return sub.alternativesResult ?? sub.lastResult;
};
export const selectPackPlannerLoading = (state: RootState) =>
  state.packAnalysisWorkbench.packPlanner.isSimulating;
/**
 * Legacy "any error" selector — surfaces a non-null string ONLY when BOTH
 * dual-mode requests failed (per Req 11.7 — hard-error Alert path). Falls
 * back to the legacy `error` field for any path that still writes there.
 */
export const selectPackPlannerError = (state: RootState) => {
  const sub = state.packAnalysisWorkbench.packPlanner;
  if (sub.progressiveError && sub.alternativesError) {
    // Both failed — surface a combined message.
    return `${sub.progressiveError} · ${sub.alternativesError}`;
  }
  return sub.error;
};

// ── Spec pack-planner-build-timeline Phase A.4.2 — dual-mode selectors ──
export const selectPackPlannerProgressiveResult = (state: RootState) =>
  state.packAnalysisWorkbench.packPlanner.progressiveResult;
export const selectPackPlannerAlternativesResult = (state: RootState) =>
  state.packAnalysisWorkbench.packPlanner.alternativesResult;
export const selectPackPlannerIsLoadingProgressive = (state: RootState) =>
  state.packAnalysisWorkbench.packPlanner.isLoadingProgressive;
export const selectPackPlannerIsLoadingAlternatives = (state: RootState) =>
  state.packAnalysisWorkbench.packPlanner.isLoadingAlternatives;
export const selectPackPlannerProgressiveError = (state: RootState) =>
  state.packAnalysisWorkbench.packPlanner.progressiveError;
export const selectPackPlannerAlternativesError = (state: RootState) =>
  state.packAnalysisWorkbench.packPlanner.alternativesError;

export const selectPackPlannerPoHeader = (state: RootState) =>
  state.packAnalysisWorkbench.packPlanner.poHeader;
export const selectPackPlannerCriteria = (state: RootState) =>
  state.packAnalysisWorkbench.packPlanner.criteria;
export const selectPackPlannerPoLines = (state: RootState) =>
  state.packAnalysisWorkbench.packPlanner.poLines;
export const selectPackPlannerPickerOpen = (state: RootState) =>
  state.packAnalysisWorkbench.packPlanner.pickerOpen;
export const selectPackPlannerItemMasterPickerOpen = (state: RootState) =>
  state.packAnalysisWorkbench.packPlanner.itemMasterPickerOpen;
export const selectPackPlannerPickerTargetLineId = (state: RootState) =>
  state.packAnalysisWorkbench.packPlanner.pickerTargetLineId;
export const selectPackPlannerPrepackGroups = (state: RootState) =>
  state.packAnalysisWorkbench.packPlanner.prepackGroups;
export const selectPackPlannerItemSearch = (state: RootState) =>
  state.packAnalysisWorkbench.packPlanner.itemSearch;

// Memoized — flatten the editor lines to the cartonization request's `lines[]`
// (strip `_*` FE-only fields, drop blank / qty ≤ 0 rows). Mirrors
// `selectSimulatorWireLines` for the parallel sub-slice.
export const selectPackPlannerWireLines = createSelector(
  [selectPackPlannerPoLines],
  (poLines) => flattenToWireLines(poLines)
);
